import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="checkout--payment"
export default class extends Controller {
  static targets = [ "creditCardForm", "submitButton", "jsWarning" ]

  get creditCardController() {
    return this.application.getControllerForElementAndIdentifier(this.creditCardFormTarget, 'checkout--credit-card')
  }

  connect() {
    // This controller running at all is the proof that the page's JS loaded, so
    // drop the "page didn't load" alert before the timer in _js_warning.html.slim
    // can reveal it.
    if (this.hasJsWarningTarget) {
      this.jsWarningTarget.remove()
    }

    this.isSubmitting = false
    this.handleFetchError = this.handleFetchError.bind(this)
    this.handleSubmitEnd = this.handleSubmitEnd.bind(this)
    this.handleFrameLoading = this.handleFrameLoading.bind(this)
    this.handleFrameLoaded = this.handleFrameLoaded.bind(this)
    this.element.addEventListener('turbo:fetch-request-error', this.handleFetchError)
    this.element.addEventListener('turbo:submit-end', this.handleSubmitEnd)
    this.element.addEventListener('turbo:before-fetch-request', this.handleFrameLoading)
    this.element.addEventListener('turbo:frame-load', this.handleFrameLoaded)
  }

  disconnect() {
    this.element.removeEventListener('turbo:fetch-request-error', this.handleFetchError)
    this.element.removeEventListener('turbo:submit-end', this.handleSubmitEnd)
    this.element.removeEventListener('turbo:before-fetch-request', this.handleFrameLoading)
    this.element.removeEventListener('turbo:frame-load', this.handleFrameLoaded)
  }

  handleSubmitEnd() {
    this.isSubmitting = false

    if (this.hasCreditCardFormTarget) {
      this.creditCardController.releaseFieldsForSubmit()
    }
  }

  handleFrameLoading(event) {
    // Only handle turbo-frame navigations, not form submissions
    if (!event.target.closest('turbo-frame')) return

    this.isFrameLoading = true
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTargets.forEach(button => button.disabled = true)
    }
  }

  handleFrameLoaded() {
    this.isFrameLoading = false
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTargets.forEach(button => button.disabled = false)
    }
  }

  handleFetchError() {
    this.isSubmitting = false

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTargets.forEach(button => {
        this.application.getControllerForElementAndIdentifier(button, 'forms--submit').completed()
      })
    }

    if (this.hasCreditCardFormTarget) {
      this.creditCardController.releaseFieldsForSubmit()
    }

    alert('A network error occurred while processing your order. Please check your connection and try again.')
  }

  placeOrder(event) {
    event.preventDefault()

    // Prevent submission while payment form is loading or already submitting
    if (this.isSubmitting || this.isFrameLoading) {
      return
    }
    this.isSubmitting = true

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTargets.forEach( button => {
        this.application.getControllerForElementAndIdentifier(button, 'forms--submit').submitting()
      })
    }

    // if credit card is used
    if (this.hasCreditCardFormTarget) {
      // get the tokenized credit card from Authorize
      try {
        this.creditCardController.setFieldsForSubmit()
      } catch (e) {
        this.isSubmitting = false
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTargets.forEach(button => {
            this.application.getControllerForElementAndIdentifier(button, 'forms--submit').completed()
          })
        }
        alert('The credit card form failed to load properly. Please reload the page and try again.')
        throw e
      }
      this.creditCardController.tokenize()
        .then((res) => {
          // we have the CC token
          // console.log(res)
          this.element.requestSubmit()
        })
        .catch((errors) => {
          let messages

          // console.log('errors:', errors)

          if (typeof errors === 'string') {
            messages = [errors]
          } else if (Array.isArray(errors)) {
            messages = errors.map((err) => err.text)
          } else if (errors instanceof Error) {
            messages = [errors.message]
          } else if (errors && typeof errors === 'object' && errors.text) {
            messages = [errors.text]
          } else {
            messages = ['An error occurred while processing your payment. Please try again.']
          }

          alert(messages.join("\n"))

          this.isSubmitting = false
          this.creditCardController.releaseFieldsForSubmit()

          if (this.hasSubmitButtonTarget) {
            this.submitButtonTargets.forEach(button => {
              this.application.getControllerForElementAndIdentifier(button, 'forms--submit').completed()
            })
          }
        })
    } else {
      this.element.requestSubmit()
    }
  }
}
